// pages/CoinMinting.jsx
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  alpha,
  Stack,
  Divider,
  Zoom,
  Fade,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
  Tooltip,
  IconButton,
  Avatar,
  AvatarGroup,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useState } from 'react';
import { keyframes } from '@mui/system';
import {
  MonetizationOn,
  Verified,
  Security,
  Diamond,
  Factory,
  PrecisionManufacturing,
  WorkspacePremium,
  Shield,
  EmojiEvents,
  TrendingUp,
  Groups,
  Timeline,
  GppGood,
  CheckCircle,
  Speed,
  LocalShipping,
  History,
  Star,
  Favorite,
  FavoriteBorder,
  QrCode,
  Approval,
  Engineering,
  WaterDrop,
  Forest,
  Handshake,
  People,
  LocationOn,
  Print,
  Brush,
  Palette,
  AutoAwesome,
  MilitaryTech,
  CurrencyExchange,
  Storefront,
  Public,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

// Premium animations
const goldShimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const floatSlow = keyframes`
  0% {
    transform: translate(0px, 0px) rotate(0deg);
  }
  33% {
    transform: translate(20px, -15px) rotate(2deg);
  }
  66% {
    transform: translate(-10px, 10px) rotate(-1deg);
  }
  100% {
    transform: translate(0px, 0px) rotate(0deg);
  }
`;

const floatMedium = keyframes`
  0% {
    transform: translate(0px, 0px) rotate(0deg);
  }
  33% {
    transform: translate(-15px, -10px) rotate(-2deg);
  }
  66% {
    transform: translate(15px, 8px) rotate(1deg);
  }
  100% {
    transform: translate(0px, 0px) rotate(0deg);
  }
`;

const floatFast = keyframes`
  0% {
    transform: translate(0px, 0px) scale(1);
  }
  50% {
    transform: translate(8px, -8px) scale(1.05);
  }
  100% {
    transform: translate(0px, 0px) scale(1);
  }
`;

const shimmerBackground = keyframes`
  0% {
    opacity: 0.3;
  }
  50% {
    opacity: 0.7;
  }
  100% {
    opacity: 0.3;
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 0.6;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.1);
  }
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

// Abstract Background Component
const AbstractBackground = () => (
  <Box
    sx={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 0,
      overflow: 'hidden',
      pointerEvents: 'none',
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 20% 30%, #0a0a2a 0%, #0d0d35 50%, #060620 100%)',
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        top: '10%',
        left: '-5%',
        width: '40%',
        height: '40%',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(212,175,55,0.15) 0%, rgba(212,175,55,0) 70%)',
        animation: `${floatSlow} 25s ease-in-out infinite`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        bottom: '5%',
        right: '-5%',
        width: '50%',
        height: '50%',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(212,175,55,0.12) 0%, rgba(212,175,55,0) 70%)',
        animation: `${floatMedium} 30s ease-in-out infinite`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        top: '30%',
        right: '10%',
        width: '300px',
        height: '300px',
        background: 'radial-gradient(circle, rgba(0,100,255,0.08) 0%, rgba(0,100,255,0) 70%)',
        borderRadius: '50%',
        animation: `${floatFast} 20s ease-in-out infinite`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        bottom: '20%',
        left: '5%',
        width: '250px',
        height: '250px',
        background: 'radial-gradient(circle, rgba(0,150,255,0.06) 0%, rgba(0,150,255,0) 70%)',
        borderRadius: '50%',
        animation: `${floatSlow} 22s ease-in-out infinite reverse`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        top: '15%',
        left: '20%',
        width: '200px',
        height: '2px',
        background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.3), transparent)',
        transform: 'rotate(25deg)',
        animation: `${shimmerBackground} 4s ease-in-out infinite`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        bottom: '25%',
        right: '15%',
        width: '300px',
        height: '2px',
        background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.25), transparent)',
        transform: 'rotate(-15deg)',
        animation: `${shimmerBackground} 5s ease-in-out infinite 1s`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        top: '60%',
        left: '10%',
        width: '150px',
        height: '150px',
        border: '1px solid rgba(212,175,55,0.08)',
        borderRadius: '50%',
        animation: `${floatMedium} 18s ease-in-out infinite`,
      }}
    />
    
    <Box
      sx={{
        position: 'absolute',
        top: '20%',
        right: '25%',
        width: '100px',
        height: '100px',
        border: '1px solid rgba(0,150,255,0.1)',
        borderRadius: '50%',
        animation: `${floatFast} 15s ease-in-out infinite reverse`,
      }}
    />
    
    {[...Array(20)].map((_, i) => (
      <Box
        key={i}
        sx={{
          position: 'absolute',
          top: `${Math.random() * 100}%`,
          left: `${Math.random() * 100}%`,
          width: `${2 + Math.random() * 4}px`,
          height: `${2 + Math.random() * 4}px`,
          background: `rgba(212, 175, 55, ${0.1 + Math.random() * 0.3})`,
          borderRadius: '50%',
          animation: `${floatFast} ${15 + Math.random() * 20}s ease-in-out infinite`,
          opacity: 0.6,
        }}
      />
    ))}
    
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: 'linear-gradient(rgba(212,175,55,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.02) 1px, transparent 1px)',
        backgroundSize: '50px 50px',
        pointerEvents: 'none',
      }}
    />
  </Box>
);

// Premium Section Header
const PremiumHeader = ({ title, subtitle, align = 'center' }) => (
  <Box sx={{ mb: 5, textAlign: align }}>
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        mb: 2,
        px: 2,
        py: 0.5,
        borderRadius: 4,
        background: alpha('#D4AF37', 0.12),
        border: '1px solid rgba(212, 175, 55, 0.25)',
        backdropFilter: 'blur(5px)',
      }}
    >
      <Diamond sx={{ fontSize: 12, color: '#D4AF37' }} />
      <Typography
        sx={{
          color: '#D4AF37',
          fontWeight: 600,
          letterSpacing: 2,
          fontSize: '0.65rem',
          textTransform: 'uppercase',
        }}
      >
        {subtitle}
      </Typography>
      <Diamond sx={{ fontSize: 12, color: '#D4AF37' }} />
    </Box>
    
    <Typography
      variant="h3"
      sx={{
        fontSize: { xs: 28, sm: 34, md: 40 },
        fontWeight: 800,
        background: 'linear-gradient(135deg, #FFFFFF 0%, #D4AF37 50%, #FFD700 100%)',
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        mb: 1.5,
        position: 'relative',
        display: 'inline-block',
        letterSpacing: '-0.02em',
      }}
    >
      {title}
    </Typography>
    
    <Box
      sx={{
        width: 60,
        height: 3,
        background: 'linear-gradient(90deg, #D4AF37, #FFD700, #D4AF37)',
        margin: '0 auto',
        borderRadius: 2,
        mt: 2,
      }}
    />
  </Box>
);

const CoinMinting = () => {
  const [tabValue, setTabValue] = useState(0);
  const [favorites, setFavorites] = useState([]);

  const toggleFavorite = (coinName) => {
    setFavorites(prev => 
      prev.includes(coinName) 
        ? prev.filter(name => name !== coinName)
        : [...prev, coinName]
    );
  };

  const coinCollections = [
    {
      name: 'Pentillion Gold Eagle',
      metal: 'Gold',
      purity: '99.99%',
      weight: '1 oz',
      year: '2024',
      mintage: '50,000',
      premium: '3%',
      description: 'A masterpiece of precision minting featuring the iconic eagle design',
      features: ['GIA Certified', 'Limited Edition', 'Anti-Counterfeit'],
      popularity: 98,
      denomination: '$50 USD',
      thickness: '2.8mm',
      diameter: '32.7mm',
    },
    {
      name: 'Pentillion Silver Maple',
      metal: 'Silver',
      purity: '99.99%',
      weight: '1 oz',
      year: '2024',
      mintage: '250,000',
      premium: '2%',
      description: 'The world\'s purest silver bullion coin with innovative anti-counterfeiting features',
      features: ['Radial Lines', 'Micro-Engraved', 'Mint Shield'],
      popularity: 95,
      denomination: '$5 CAD',
      thickness: '3.15mm',
      diameter: '38mm',
    },
    {
      name: 'Pentillion Platinum Philharmonic',
      metal: 'Platinum',
      purity: '99.95%',
      weight: '1 oz',
      year: '2024',
      mintage: '25,000',
      premium: '4%',
      description: 'Celebrating musical heritage with exceptional platinum craftsmanship',
      features: ['Proof Finish', 'Musical Motif', 'Low Mintage'],
      popularity: 92,
      denomination: '$100 USD',
      thickness: '2.5mm',
      diameter: '32mm',
    },
    {
      name: 'Pentillion Palladium Panda',
      metal: 'Palladium',
      purity: '99.95%',
      weight: '1 oz',
      year: '2024',
      mintage: '10,000',
      premium: '5%',
      description: 'Limited edition palladium coin featuring the beloved panda',
      features: ['Reverse Proof', 'Bamboo Design', 'Rare Metal'],
      popularity: 89,
      denomination: '$75 USD',
      thickness: '2.3mm',
      diameter: '34mm',
    },
  ];

  const barProducts = [
    { name: 'Gold Cast Bar', purity: '99.99%', weight: '1 kg', dimensions: '117.5 x 52.5 x 12 mm', metal: 'Gold', features: ['Serialized', 'LBMA Accredited'], annualProduction: '50,000 units' },
    { name: 'Gold Minted Bar', purity: '99.99%', weight: '100 g', dimensions: '47 x 27 x 5 mm', metal: 'Gold', features: ['Certificate', 'Assay Office'], annualProduction: '200,000 units' },
    { name: 'Silver Cast Bar', purity: '99.9%', weight: '5 kg', dimensions: '180 x 90 x 25 mm', metal: 'Silver', features: ['Industrial Grade', 'Bulk Packaging'], annualProduction: '25,000 units' },
    { name: 'Platinum Bar', purity: '99.95%', weight: '10 g', dimensions: '24 x 14 x 2 mm', metal: 'Platinum', features: ['Investment Grade', 'Sealed Package'], annualProduction: '100,000 units' },
  ];

  const limitedEditions = [
    { name: '25th Anniversary Diamond Coin', metal: 'Gold', purity: '99.99%', weight: '2 oz', mintage: '2,500', premium: '8%', special: 'Diamond-Embedded', releaseDate: 'Q2 2024' },
    { name: 'Year of the Dragon', metal: 'Gold', purity: '99.99%', weight: '1 oz', mintage: '5,000', premium: '5%', special: 'Colorized', releaseDate: 'Q1 2024' },
    { name: 'African Wildlife Series', metal: 'Silver', purity: '99.9%', weight: '2 oz', mintage: '15,000', premium: '4%', special: 'Antique Finish', releaseDate: 'Q3 2024' },
    { name: 'World Heritage Collection', metal: 'Platinum', purity: '99.95%', weight: '1 oz', mintage: '3,000', premium: '6%', special: 'High Relief', releaseDate: 'Q4 2024' },
  ];

  // REMOVED: metrics array - no longer displaying the 4 metrics cards

  const capabilities = [
    { icon: <PrecisionManufacturing />, title: 'Precision Minting', description: '0.001mm tolerance', metric: '99.99% Accuracy', annualCapacity: '3M coins' },
    { icon: <Security />, title: 'Security Features', description: 'Anti-counterfeiting technology', metric: '15+ Features', annualCapacity: 'All products' },
    { icon: <Verified />, title: 'Certified Purity', description: 'Independent verification', metric: 'LBMA/ISO Certified', annualCapacity: '100% of products' },
    { icon: <Shield />, title: 'Secure Storage', description: 'Vault-grade protection', metric: '24/7/365 Security', annualCapacity: '50,000 sq ft' },
    { icon: <Speed />, title: 'Rapid Production', description: 'High-volume minting', metric: '50K coins/day', annualCapacity: '15M coins' },
    { icon: <LocalShipping />, title: 'Global Delivery', description: 'Insured shipping', metric: '50+ Countries', annualCapacity: 'Same-day dispatch' },
  ];

  const customServices = [
    'Private label coin minting for institutions',
    'Commemorative coins and medals',
    'Corporate gifts and awards',
    'Investment bars in bespoke sizes',
    'Design consultation and prototyping',
    'Serialized certification',
    'Custom packaging solutions',
    'Blockchain registration',
    'Proof and reverse proof finishes',
    'Colorized and hologram options',
  ];

  const securityFeatures = [
    { name: 'Micro-Engraving', description: 'Invisible text visible only under magnification', level: 'Level 3', icon: <QrCode /> },
    { name: 'Radial Lines', description: 'Interlocking geometric patterns', level: 'Level 2', icon: <AutoAwesome /> },
    { name: 'Prismatic Effect', description: 'Color-shifting technology', level: 'Level 3', icon: <Palette /> },
    { name: 'Laser Marking', description: 'Unique serial numbers', level: 'Level 2', icon: <Print /> },
    { name: 'Edge Lettering', description: 'Inscribed security text on rim', level: 'Level 1', icon: <MilitaryTech /> },
  ];

  const mintingProcess = [
    { step: 1, name: 'Design & Engraving', description: 'Master die creation', duration: '4-6 weeks', icon: <Brush /> },
    { step: 2, name: 'Metal Preparation', description: 'Refining and alloying', duration: '1 week', icon: <PrecisionManufacturing /> },
    { step: 3, name: 'Blank Production', description: 'Cutting and annealing', duration: '1 week', icon: <Factory /> },
    { step: 4, name: 'Minting', description: 'High-pressure striking', duration: '2-3 days', icon: <WorkspacePremium /> },
    { step: 5, name: 'Quality Control', description: 'XRF and visual inspection', duration: '2 days', icon: <Verified /> },
  
  ];

  const getMetalColor = (metal) => {
    switch(metal) {
      case 'Gold': return '#D4AF37';
      case 'Silver': return '#C0C0C0';
      case 'Platinum': return '#E5E4E2';
      case 'Palladium': return '#CED0D6';
      default: return '#D4AF37';
    }
  };

  const getMetalGradient = (metal) => {
    switch(metal) {
      case 'Gold': return 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)';
      case 'Silver': return 'linear-gradient(135deg, #C0C0C0 0%, #E8E8E8 100%)';
      case 'Platinum': return 'linear-gradient(135deg, #E5E4E2 0%, #FFFFFF 100%)';
      case 'Palladium': return 'linear-gradient(135deg, #CED0D6 0%, #E2E4E9 100%)';
      default: return 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)';
    }
  };

  return (
    <Box sx={{ position: 'relative', minHeight: '100vh', overflowX: 'hidden' }}>
      <AbstractBackground />
      
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        {/* Hero Section with Minting Background Image */}
        <Box
          sx={{
            minHeight: '55vh',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            backgroundImage: 'url("/images/minting.jpg")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.7) 50%, rgba(0,0,0,0.85) 100%)',
              zIndex: 0,
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '2px',
              background: 'linear-gradient(90deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
              zIndex: 1,
            },
          }}
        >
          <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2, py: { xs: 8, md: 10 } }}>
            <Stack spacing={2.5}>
              <Chip
                label="PREMIUM MINTING FACILITY"
                icon={<Factory sx={{ fontSize: 14 }} />}
                sx={{
                  bgcolor: alpha('#D4AF37', 0.2),
                  color: '#D4AF37',
                  width: 'fit-content',
                  letterSpacing: 1.5,
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  border: '1px solid rgba(212, 175, 55, 0.4)',
                  backdropFilter: 'blur(5px)',
                }}
              />
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem', lg: '4rem' },
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #D4AF37 50%, #FFD700 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '-0.02em',
                }}
              >
                World-Class Bullion & Coin Minting
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: '#D4AF37',
                  fontSize: { xs: '1rem', sm: '1.2rem', md: '1.3rem' },
                  fontWeight: 600,
                }}
              >
                Turning Precious Metals into Masterpieces
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: alpha('#FFFFFF', 0.85),
                  maxWidth: 700,
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                }}
              >
                State-of-the-art minting facility producing LBMA-accredited bullion, collector coins, 
                and bespoke minting solutions for global institutions and private clients
              </Typography>
            </Stack>
          </Container>
        </Box>

        {/* Metrics Dashboard Section - REMOVED */}

        {/* Minting Facility Overview */}
        <Box sx={{ bgcolor: '#FFFFFF', py: 6 }}>
          <Container maxWidth="xl">
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box sx={{ animation: `${fadeInLeft} 0.6s ease-out forwards`, opacity: 0 }}>
                  <Chip
                    label="FACILITY OVERVIEW"
                    icon={<Factory sx={{ fontSize: 14 }} />}
                    sx={{
                      bgcolor: alpha('#D4AF37', 0.1),
                      color: '#D4AF37',
                      fontWeight: 700,
                      letterSpacing: 1.5,
                      mb: 2,
                    }}
                  />
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, md: 36 },
                      fontWeight: 800,
                      color: '#0a0a2a',
                      mb: 2,
                    }}
                  >
                    State-of-the-Art Minting Facility
                  </Typography>
                  <Typography variant="body1" sx={{ color: alpha('#0a0a2a', 0.7), mb: 3, lineHeight: 1.7 }}>
                    Our minting facility is equipped with the latest German and Swiss minting technology, 
                    capable of producing up to 5,000 coins and 1,000 bars daily.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <LocationOn sx={{ color: '#D4AF37' }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>Location</Typography>
                          <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5) }}>Eswatini / South Africa Hub</Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <People sx={{ color: '#D4AF37' }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>Workforce</Typography>
                          <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5) }}>250+ Specialists</Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Speed sx={{ color: '#D4AF37' }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>Annual Capacity</Typography>
                          <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5) }}>15M+ Units</Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Shield sx={{ color: '#D4AF37' }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>Security Level</Typography>
                          <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5) }}>Military-Grade</Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ animation: `${fadeInRight} 0.6s ease-out 0.2s forwards`, opacity: 0 }}>
                  <Card
                    elevation={0}
                    sx={{
                      p: 3,
                      background: `linear-gradient(135deg, ${alpha('#D4AF37', 0.03)} 0%, ${alpha('#D4AF37', 0.01)} 100%)`,
                      border: `1px solid ${alpha('#D4AF37', 0.15)}`,
                      borderRadius: 3,
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Production Capacity</Typography>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption">Coins (Annual)</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>2.5M+ oz</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={85} sx={{ height: 6, borderRadius: 3, bgcolor: alpha('#D4AF37', 0.1), '& .MuiLinearProgress-bar': { bgcolor: '#D4AF37', borderRadius: 3 } }} />
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption">Bars (Annual)</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>500,000+ units</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={75} sx={{ height: 6, borderRadius: 3, bgcolor: alpha('#D4AF37', 0.1), '& .MuiLinearProgress-bar': { bgcolor: '#D4AF37', borderRadius: 3 } }} />
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption">Custom Orders</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>500+ projects</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={90} sx={{ height: 6, borderRadius: 3, bgcolor: alpha('#D4AF37', 0.1), '& .MuiLinearProgress-bar': { bgcolor: '#D4AF37', borderRadius: 3 } }} />
                    </Box>
                  </Card>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Minting Process Timeline */}
        <Box sx={{ py: { xs: 8, md: 10 }, position: 'relative' }}>
          <Container maxWidth="xl">
            <PremiumHeader title="The Minting Process" subtitle="FROM DESIGN TO DELIVERY" />
            <Typography variant="body2" sx={{ color: alpha('#FFFFFF', 0.6), maxWidth: 700, mx: 'auto', textAlign: 'center', mb: 5 }}>
              Our precision minting workflow ensures exceptional quality at every stage
            </Typography>

            <Grid container spacing={3}>
              {mintingProcess.map((step, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Box
                    sx={{
                      animation: `${scaleIn} 0.5s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.1}s forwards`,
                      opacity: 0,
                    }}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        bgcolor: alpha('#FFFFFF', 0.05),
                        backdropFilter: 'blur(10px)',
                        border: `1px solid ${alpha('#D4AF37', 0.15)}`,
                        borderRadius: 3,
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          borderColor: alpha('#D4AF37', 0.4),
                        },
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -15,
                          left: 20,
                          width: 30,
                          height: 30,
                          borderRadius: '50%',
                          bgcolor: '#D4AF37',
                          color: '#0a0a2a',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 800,
                          fontSize: '0.9rem',
                        }}
                      >
                        {step.step}
                      </Box>
                      <Box sx={{ color: '#D4AF37', fontSize: 40, mb: 1.5 }}>{step.icon}</Box>
                      <Typography variant="h6" sx={{ color: '#D4AF37', fontWeight: 800, mb: 0.5 }}>
                        {step.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#FFFFFF', mb: 1 }}>
                        {step.description}
                      </Typography>
                      <Chip
                        label={`Duration: ${step.duration}`}
                        size="small"
                        sx={{
                          bgcolor: alpha('#D4AF37', 0.15),
                          color: '#D4AF37',
                          fontSize: '0.6rem',
                        }}
                      />
                    </Card>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Products Section */}
        <Box sx={{ bgcolor: '#FFFFFF', py: { xs: 8, md: 10 }, position: 'relative' }}>
          <Container maxWidth="xl">
            <PremiumHeader title="Premium Products" subtitle="OUR COLLECTIONS" />

            <Paper
              elevation={0}
              sx={{
                mt: 4,
                bgcolor: '#FFFFFF',
                border: `1px solid ${alpha('#D4AF37', 0.15)}`,
                borderRadius: 3,
                overflow: 'hidden',
              }}
            >
              <Tabs
                value={tabValue}
                onChange={(e, v) => setTabValue(v)}
                centered
                sx={{
                  borderBottom: `1px solid ${alpha('#D4AF37', 0.15)}`,
                  '& .MuiTab-root': {
                    color: alpha('#0a0a2a', 0.6),
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    py: 2,
                    '&.Mui-selected': {
                      color: '#D4AF37',
                    },
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#D4AF37',
                    height: 3,
                  },
                }}
              >
                <Tab label="Premium Coins" />
                <Tab label="Investment Bars" />
                <Tab label="Limited Editions" />
                <Tab label="Custom Minting" />
              </Tabs>

              <Box sx={{ p: 4 }}>
                {tabValue === 0 && (
                  <Grid container spacing={3}>
                    {coinCollections.map((coin, index) => (
                      <Grid item xs={12} md={6} key={index}>
                        <Box
                          sx={{
                            animation: `${scaleIn} 0.6s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.1}s forwards`,
                            opacity: 0,
                          }}
                        >
                          <Card
                            elevation={0}
                            sx={{
                              bgcolor: '#F8F9FC',
                              border: `1px solid ${alpha(getMetalColor(coin.metal), 0.2)}`,
                              borderRadius: 3,
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-6px)',
                                borderColor: getMetalColor(coin.metal),
                                boxShadow: `0 12px 25px ${alpha(getMetalColor(coin.metal), 0.15)}`,
                              },
                            }}
                          >
                            <CardContent sx={{ p: 3 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box>
                                  <Typography variant="h6" sx={{ color: getMetalColor(coin.metal), fontWeight: 800, mb: 0.5 }}>
                                    {coin.name}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#D4AF37', fontWeight: 600 }}>
                                    {coin.denomination}
                                  </Typography>
                                </Box>
                                <Tooltip title="Add to wishlist">
                                  <IconButton size="small" onClick={() => toggleFavorite(coin.name)}>
                                    {favorites.includes(coin.name) ? 
                                      <Favorite sx={{ color: '#D4AF37', fontSize: 20 }} /> : 
                                      <FavoriteBorder sx={{ fontSize: 20 }} />
                                    }
                                  </IconButton>
                                </Tooltip>
                              </Box>
                              
                              <Divider sx={{ my: 2, borderColor: alpha('#D4AF37', 0.15) }} />
                              
                              <Grid container spacing={2}>
                                <Grid item xs={6}>
                                  <Typography variant="caption" sx={{ color: '#D4AF37', fontWeight: 600 }}>Purity</Typography>
                                  <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>{coin.purity}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="caption" sx={{ color: '#D4AF37', fontWeight: 600 }}>Weight</Typography>
                                  <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>{coin.weight}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="caption" sx={{ color: '#D4AF37', fontWeight: 600 }}>Diameter</Typography>
                                  <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>{coin.diameter}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="caption" sx={{ color: '#D4AF37', fontWeight: 600 }}>Thickness</Typography>
                                  <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>{coin.thickness}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="caption" sx={{ color: '#D4AF37', fontWeight: 600 }}>Year</Typography>
                                  <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>{coin.year}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="caption" sx={{ color: '#D4AF37', fontWeight: 600 }}>Mintage</Typography>
                                  <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>{coin.mintage}</Typography>
                                </Grid>
                              </Grid>
                              
                              <Typography variant="body2" sx={{ color: alpha('#0a0a2a', 0.6), mt: 2 }}>
                                {coin.description}
                              </Typography>
                              
                              <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                                {coin.features.map((feature, idx) => (
                                  <Chip
                                    key={idx}
                                    label={feature}
                                    size="small"
                                    sx={{
                                      bgcolor: alpha('#D4AF37', 0.08),
                                      color: '#D4AF37',
                                      fontSize: '0.6rem',
                                      height: 22,
                                    }}
                                  />
                                ))}
                              </Box>
                              
                              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                                <Button
                                  variant="contained"
                                  size="small"
                                  sx={{
                                    flex: 1,
                                    background: getMetalGradient(coin.metal),
                                    color: '#0a0a2a',
                                    fontWeight: 700,
                                    '&:hover': { transform: 'translateY(-2px)' },
                                  }}
                                >
                                  Buy Now
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  sx={{
                                    flex: 1,
                                    borderColor: '#D4AF37',
                                    color: '#D4AF37',
                                    fontWeight: 600,
                                  }}
                                >
                                  Request Quote
                                </Button>
                              </Box>
                            </CardContent>
                          </Card>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}

                {tabValue === 1 && (
                  <Grid container spacing={3}>
                    {barProducts.map((bar, index) => (
                      <Grid item xs={12} sm={6} md={6} key={index}>
                        <Box
                          sx={{
                            animation: `${scaleIn} 0.6s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.1}s forwards`,
                            opacity: 0,
                          }}
                        >
                          <Card
                            elevation={0}
                            sx={{
                              bgcolor: '#F8F9FC',
                              border: `1px solid ${alpha(getMetalColor(bar.metal), 0.2)}`,
                              borderRadius: 3,
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-6px)',
                                borderColor: getMetalColor(bar.metal),
                                boxShadow: `0 12px 25px ${alpha(getMetalColor(bar.metal), 0.15)}`,
                              },
                            }}
                          >
                            <CardContent sx={{ p: 3 }}>
                              <Typography variant="h6" sx={{ color: getMetalColor(bar.metal), fontWeight: 800, mb: 2 }}>
                                {bar.name}
                              </Typography>
                              <Divider sx={{ mb: 2, borderColor: alpha('#D4AF37', 0.15) }} />
                              <Grid container spacing={2}>
                                <Grid item xs={6}>
                                  <Typography variant="caption" sx={{ color: '#D4AF37', fontWeight: 600 }}>Purity</Typography>
                                  <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>{bar.purity}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="caption" sx={{ color: '#D4AF37', fontWeight: 600 }}>Weight</Typography>
                                  <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>{bar.weight}</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                  <Typography variant="caption" sx={{ color: '#D4AF37', fontWeight: 600 }}>Dimensions</Typography>
                                  <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>{bar.dimensions}</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                  <Typography variant="caption" sx={{ color: '#D4AF37', fontWeight: 600 }}>Annual Production</Typography>
                                  <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>{bar.annualProduction}</Typography>
                                </Grid>
                              </Grid>
                              <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                                {bar.features.map((feature, idx) => (
                                  <Chip
                                    key={idx}
                                    label={feature}
                                    size="small"
                                    sx={{
                                      bgcolor: alpha('#D4AF37', 0.08),
                                      color: '#D4AF37',
                                      fontSize: '0.6rem',
                                      height: 22,
                                    }}
                                  />
                                ))}
                              </Box>
                              <Button
                                variant="outlined"
                                size="small"
                                fullWidth
                                sx={{
                                  mt: 3,
                                  borderColor: '#D4AF37',
                                  color: '#D4AF37',
                                  fontWeight: 600,
                                  '&:hover': {
                                    borderColor: '#D4AF37',
                                    bgcolor: alpha('#D4AF37', 0.05),
                                  },
                                }}
                              >
                                Request Pricing
                              </Button>
                            </CardContent>
                          </Card>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}

                {tabValue === 2 && (
                  <Grid container spacing={3}>
                    {limitedEditions.map((edition, index) => (
                      <Grid item xs={12} sm={6} md={6} key={index}>
                        <Box
                          sx={{
                            animation: `${scaleIn} 0.6s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.1}s forwards`,
                            opacity: 0,
                          }}
                        >
                          <Card
                            elevation={0}
                            sx={{
                              bgcolor: '#F8F9FC',
                              border: `2px solid ${alpha(getMetalColor(edition.metal), 0.3)}`,
                              borderRadius: 3,
                              position: 'relative',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-6px)',
                                borderColor: getMetalColor(edition.metal),
                                boxShadow: `0 12px 25px ${alpha(getMetalColor(edition.metal), 0.2)}`,
                              },
                            }}
                          >
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 10,
                                right: 10,
                                bgcolor: '#D4AF37',
                                color: '#0a0a2a',
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                fontSize: '0.6rem',
                                fontWeight: 700,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              <Star sx={{ fontSize: 12 }} />
                              Limited Edition
                            </Box>
                            
                            <CardContent sx={{ p: 3 }}>
                              <Typography variant="h6" sx={{ color: getMetalColor(edition.metal), fontWeight: 800, mb: 2 }}>
                                {edition.name}
                              </Typography>
                              <Divider sx={{ mb: 2, borderColor: alpha('#D4AF37', 0.15) }} />
                              <Grid container spacing={2}>
                                <Grid item xs={6}>
                                  <Typography variant="caption" sx={{ color: '#D4AF37', fontWeight: 600 }}>Metal</Typography>
                                  <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>{edition.metal}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="caption" sx={{ color: '#D4AF37', fontWeight: 600 }}>Weight</Typography>
                                  <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>{edition.weight}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="caption" sx={{ color: '#D4AF37', fontWeight: 600 }}>Mintage</Typography>
                                  <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>{edition.mintage}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="caption" sx={{ color: '#D4AF37', fontWeight: 600 }}>Release</Typography>
                                  <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>{edition.releaseDate}</Typography>
                                </Grid>
                              </Grid>
                              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                <Chip
                                  label={edition.special}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha('#D4AF37', 0.15),
                                    color: '#D4AF37',
                                    fontWeight: 600,
                                  }}
                                />
                                <Chip
                                  label={`Premium: ${edition.premium}`}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha('#4caf50', 0.1),
                                    color: '#4caf50',
                                  }}
                                />
                              </Box>
                              <Button
                                variant="contained"
                                size="small"
                                fullWidth
                                sx={{
                                  mt: 3,
                                  background: getMetalGradient(edition.metal),
                                  color: '#0a0a2a',
                                  fontWeight: 700,
                                  '&:hover': { transform: 'translateY(-2px)' },
                                }}
                              >
                                Reserve Now
                              </Button>
                            </CardContent>
                          </Card>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}

                {tabValue === 3 && (
                  <Box
                    sx={{
                      animation: `${fadeInUp} 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards`,
                      opacity: 0,
                    }}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        bgcolor: '#F8F9FC',
                        border: `1px solid ${alpha('#D4AF37', 0.2)}`,
                        borderRadius: 3,
                      }}
                    >
                      <CardContent sx={{ p: 4 }}>
                        <Typography variant="h5" sx={{ color: '#0a0a2a', fontWeight: 800, mb: 2 }}>
                          Bespoke Minting Services
                        </Typography>
                        <Typography variant="body1" sx={{ color: alpha('#0a0a2a', 0.7), mb: 3 }}>
                          Pentillion offers comprehensive custom minting services for institutions, governments, and private clients.
                        </Typography>
                        <Grid container spacing={2}>
                          {customServices.map((service, index) => (
                            <Grid item xs={12} sm={6} key={index}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                <CheckCircle sx={{ color: '#D4AF37', fontSize: 16 }} />
                                <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 500 }}>{service}</Typography>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                        <Box sx={{ display: 'flex', gap: 2, mt: 4, flexWrap: 'wrap' }}>
                          <Button
                            variant="contained"
                            sx={{
                              background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)',
                              color: '#0a0a2a',
                              fontWeight: 700,
                              px: 4,
                              '&:hover': {
                                background: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 100%)',
                                transform: 'translateY(-2px)',
                              },
                            }}
                          >
                            Request Quote
                          </Button>
                          <Button
                            variant="outlined"
                            sx={{
                              borderColor: '#D4AF37',
                              color: '#D4AF37',
                              fontWeight: 700,
                              px: 4,
                              '&:hover': {
                                borderColor: '#D4AF37',
                                bgcolor: alpha('#D4AF37', 0.05),
                              },
                            }}
                          >
                            Schedule Consultation
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                )}
              </Box>
            </Paper>
          </Container>
        </Box>

        {/* Security Features Section */}
        <Box sx={{ py: { xs: 8, md: 10 }, position: 'relative' }}>
          <Container maxWidth="xl">
            <PremiumHeader title="Advanced Security Features" subtitle="PROTECTING YOUR INVESTMENT" />
            <Typography variant="body2" sx={{ color: alpha('#FFFFFF', 0.6), maxWidth: 700, mx: 'auto', textAlign: 'center', mb: 5 }}>
              Industry-leading anti-counterfeiting technology integrated into every product
            </Typography>

            <Grid container spacing={3}>
              {securityFeatures.map((feature, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Box
                    sx={{
                      animation: `${fadeInUp} 0.5s ease-out ${index * 0.1}s forwards`,
                      opacity: 0,
                    }}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        bgcolor: alpha('#FFFFFF', 0.05),
                        backdropFilter: 'blur(10px)',
                        border: `1px solid ${alpha('#D4AF37', 0.15)}`,
                        borderRadius: 3,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          borderColor: alpha('#D4AF37', 0.4),
                        },
                      }}
                    >
                      <Box sx={{ color: '#D4AF37', fontSize: 42, mb: 1.5 }}>{feature.icon}</Box>
                      <Typography variant="h6" sx={{ color: '#D4AF37', fontWeight: 800, mb: 0.5 }}>
                        {feature.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#FFFFFF', mb: 1 }}>
                        {feature.description}
                      </Typography>
                      <Chip
                        label={`Security Level ${feature.level}`}
                        size="small"
                        sx={{
                          bgcolor: alpha('#D4AF37', 0.15),
                          color: '#D4AF37',
                          fontSize: '0.6rem',
                        }}
                      />
                    </Card>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Global Reach Section */}
        <Box sx={{ bgcolor: '#FFFFFF', py: { xs: 8, md: 10 } }}>
          <Container maxWidth="xl">
            <PremiumHeader title="Global Distribution Network" subtitle="WORLDWIDE REACH" />
            
            <Grid container spacing={4}>
              <Grid item xs={12} md={4}>
                <Card
                  elevation={0}
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    bgcolor: '#F8F9FC',
                    border: `1px solid ${alpha('#D4AF37', 0.12)}`,
                    borderRadius: 3,
                  }}
                >
                  <Public sx={{ fontSize: 48, color: '#D4AF37', mb: 1.5 }} />
                  <Typography variant="h3" sx={{ color: '#D4AF37', fontWeight: 800 }}>50+</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>Countries Served</Typography>
                  <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5) }}>Global footprint</Typography>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card
                  elevation={0}
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    bgcolor: '#F8F9FC',
                    border: `1px solid ${alpha('#D4AF37', 0.12)}`,
                    borderRadius: 3,
                  }}
                >
                  <LocalShipping sx={{ fontSize: 48, color: '#D4AF37', mb: 1.5 }} />
                  <Typography variant="h3" sx={{ color: '#D4AF37', fontWeight: 800 }}>24/7</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>Global Logistics</Typography>
                  <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5) }}>Fully insured shipping</Typography>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card
                  elevation={0}
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    bgcolor: '#F8F9FC',
                    border: `1px solid ${alpha('#D4AF37', 0.12)}`,
                    borderRadius: 3,
                  }}
                >
                  <Storefront sx={{ fontSize: 48, color: '#D4AF37', mb: 1.5 }} />
                  <Typography variant="h3" sx={{ color: '#D4AF37', fontWeight: 800 }}>200+</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>Authorized Dealers</Typography>
                  <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5) }}>Global partners</Typography>
                </Card>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Capabilities Section */}
        <Box sx={{ py: { xs: 8, md: 10 }, position: 'relative' }}>
          <Container maxWidth="xl">
            <PremiumHeader title="Minting Capabilities" subtitle="OUR EXPERTISE" />

            <Grid container spacing={3}>
              {capabilities.map((cap, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Box
                    sx={{
                      animation: `${fadeInUp} 0.6s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.05}s forwards`,
                      opacity: 0,
                    }}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        height: '100%',
                        bgcolor: alpha('#FFFFFF', 0.05),
                        backdropFilter: 'blur(10px)',
                        border: `1px solid ${alpha('#D4AF37', 0.15)}`,
                        borderRadius: 3,
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-6px)',
                          borderColor: alpha('#D4AF37', 0.35),
                          boxShadow: `0 15px 30px ${alpha('#D4AF37', 0.12)}`,
                        },
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ fontSize: 44, color: '#D4AF37', mb: 2 }}>{cap.icon}</Box>
                        <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 700, mb: 1 }}>
                          {cap.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: alpha('#FFFFFF', 0.6), mb: 1.5 }}>
                          {cap.description}
                        </Typography>
                        <Chip
                          label={cap.metric}
                          size="small"
                          sx={{
                            bgcolor: alpha('#D4AF37', 0.1),
                            color: '#D4AF37',
                            fontSize: '0.65rem',
                          }}
                        />
                        <Typography variant="caption" sx={{ color: alpha('#FFFFFF', 0.4), display: 'block', mt: 1 }}>
                          Capacity: {cap.annualCapacity}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Security & Certification Section */}
        <Box sx={{ bgcolor: '#FFFFFF', py: { xs: 8, md: 10 }, position: 'relative' }}>
          <Container maxWidth="xl">
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box sx={{ animation: `${fadeInLeft} 0.6s ease-out forwards`, opacity: 0 }}>
                  <Chip
                    label="QUALITY ASSURANCE"
                    icon={<Shield sx={{ fontSize: 14 }} />}
                    sx={{
                      bgcolor: alpha('#D4AF37', 0.1),
                      color: '#D4AF37',
                      fontWeight: 700,
                      letterSpacing: 1.5,
                      mb: 2,
                    }}
                  />
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, md: 36 },
                      fontWeight: 800,
                      color: '#0a0a2a',
                      mb: 2,
                    }}
                  >
                    Uncompromising Quality & Security
                  </Typography>
                  <Typography variant="body1" sx={{ color: alpha('#0a0a2a', 0.7), mb: 3, lineHeight: 1.7 }}>
                    Every coin and bar undergoes rigorous testing and certification before leaving our mint.
                  </Typography>
                  <List>
                    {[
                      { icon: <Verified />, title: 'XRF Analysis', description: 'Real-time composition verification' },
                      { icon: <GppGood />, title: 'Fire Assay', description: 'Independent third-party testing' },
                      { icon: <QrCode />, title: 'Blockchain Tracking', description: 'End-to-end provenance' },
                      { icon: <History />, title: 'Serialized Certification', description: 'Unique identifier per product' },
                    ].map((item, idx) => (
                      <ListItem key={idx} sx={{ px: 0, py: 1.5 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <Box sx={{ color: '#D4AF37' }}>{item.icon}</Box>
                        </ListItemIcon>
                        <ListItemText
                          primary={<Typography sx={{ color: '#0a0a2a', fontWeight: 700 }}>{item.title}</Typography>}
                          secondary={<Typography sx={{ color: alpha('#0a0a2a', 0.5), fontSize: '0.75rem' }}>{item.description}</Typography>}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ animation: `${fadeInRight} 0.6s ease-out 0.2s forwards`, opacity: 0 }}>
                  <Card
                    elevation={0}
                    sx={{
                      p: 4,
                      textAlign: 'center',
                      bgcolor: '#F8F9FC',
                      border: `1px solid ${alpha('#D4AF37', 0.15)}`,
                      borderRadius: 3,
                    }}
                  >
                    <WorkspacePremium sx={{ fontSize: 56, color: '#D4AF37', mb: 2, animation: `${pulse} 2s ease-in-out infinite` }} />
                    <Typography variant="h4" sx={{ color: '#0a0a2a', fontWeight: 800, mb: 2 }}>
                      Lifetime Authenticity Guarantee
                    </Typography>
                    <Typography variant="body1" sx={{ color: alpha('#0a0a2a', 0.6), mb: 3 }}>
                      Every Pentillion product is backed by our comprehensive authenticity and purity guarantee.
                    </Typography>
                    <Divider sx={{ mb: 3, borderColor: alpha('#D4AF37', 0.15) }} />
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ color: alpha('#0a0a2a', 0.7) }}>Quality Rating</Typography>
                        <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>99.99%</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={99.99} sx={{ height: 6, borderRadius: 3, bgcolor: alpha('#D4AF37', 0.1), '& .MuiLinearProgress-bar': { bgcolor: '#D4AF37', borderRadius: 3 } }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ color: alpha('#0a0a2a', 0.7) }}>Client Satisfaction</Typography>
                        <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>98%</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={98} sx={{ height: 6, borderRadius: 3, bgcolor: alpha('#D4AF37', 0.1), '& .MuiLinearProgress-bar': { bgcolor: '#D4AF37', borderRadius: 3 } }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ color: alpha('#0a0a2a', 0.7) }}>On-Time Delivery</Typography>
                        <Typography variant="body2" sx={{ color: '#0a0a2a', fontWeight: 700 }}>99.5%</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={99.5} sx={{ height: 6, borderRadius: 3, bgcolor: alpha('#D4AF37', 0.1), '& .MuiLinearProgress-bar': { bgcolor: '#D4AF37', borderRadius: 3 } }} />
                    </Stack>
                  </Card>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Certifications Section */}
        <Box sx={{ py: { xs: 8, md: 10 }, position: 'relative' }}>
          <Container maxWidth="xl">
            <PremiumHeader title="Accreditations" subtitle="GLOBAL RECOGNITION" />
            
            <Grid container spacing={3} justifyContent="center">
              {[
                'LBMA Good Delivery',
                'LPPM Accredited',
                'ISO 9001:2024',
                'ISO 14001',
                'ISO 45001',
                'RJC Certified',
                'UN Global Compact',
                'Responsible Sourcing',
              ].map((cert, idx) => (
                <Grid item xs={6} sm={4} md={3} key={idx}>
                  <Zoom in timeout={300 + idx * 50}>
                    <Card
                      elevation={0}
                      sx={{
                        textAlign: 'center',
                        p: 2.5,
                        bgcolor: alpha('#FFFFFF', 0.05),
                        backdropFilter: 'blur(10px)',
                        border: `1px solid ${alpha('#D4AF37', 0.15)}`,
                        borderRadius: 2,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          borderColor: alpha('#D4AF37', 0.4),
                          bgcolor: alpha('#FFFFFF', 0.08),
                        },
                      }}
                    >
                      <Verified sx={{ color: '#D4AF37', fontSize: 32, mb: 1 }} />
                      <Typography variant="caption" sx={{ color: '#FFFFFF', fontWeight: 600, display: 'block' }}>
                        {cert}
                      </Typography>
                    </Card>
                  </Zoom>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* CTA Section */}
        <Box sx={{ py: 8 }}>
          <Container maxWidth="md" sx={{ textAlign: 'center' }}>
            <Card
              elevation={0}
              sx={{
                p: 5,
                bgcolor: '#FFFFFF',
                border: `1px solid ${alpha('#D4AF37', 0.2)}`,
                borderRadius: 3,
              }}
            >
              <MonetizationOn sx={{ color: '#D4AF37', fontSize: 48, mb: 2, animation: `${rotate} 3s ease-in-out infinite` }} />
              <Typography variant="h4" sx={{ color: '#0a0a2a', fontWeight: 800, mb: 2 }}>
                Ready to Mint Your Collection?
              </Typography>
              <Typography variant="body1" sx={{ color: alpha('#0a0a2a', 0.6), mb: 3 }}>
                Contact our minting team to discuss your specific requirements
              </Typography>
              <Button
                variant="contained"
                size="large"
                sx={{
                  background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)',
                  color: '#0a0a2a',
                  fontWeight: 700,
                  px: 5,
                  py: 1.5,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 20px ${alpha('#D4AF37', 0.3)}`,
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Contact Minting Team
              </Button>
            </Card>
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

export default CoinMinting;